import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Badge, Tag, Drawer, List, Button, Space, Tooltip, Spin, Empty, Typography, message, Alert } from "antd";
import { WarningOutlined, ExclamationCircleOutlined, InfoCircleOutlined, CheckCircleOutlined, SearchOutlined, SafetyCertificateOutlined, ScanOutlined, ReloadOutlined } from "@ant-design/icons";
import { flightOperationFlightService } from "../../../../api/enhancedApi";
import { useMonitor } from "../context/MonitorContext";
import { useAuth } from "../../../../hooks/useAuth";
import { FLIGHT_STATUS } from "../constants/flightConstants";
import dayjs from "dayjs";
import "./ValidationWarningCard.css";

const { Text } = Typography;

const LEVEL_CONFIG = {
    ERROR: { color: "#ff4d4f", tag: "red", icon: <ExclamationCircleOutlined />, label: "错误" },
    WARNING: { color: "#faad14", tag: "orange", icon: <WarningOutlined />, label: "警告" },
    INFO: { color: "#1890ff", tag: "blue", icon: <InfoCircleOutlined />, label: "提示" },
};

function buildDateParams(filters) {
    const params = {};
    if (filters.dateRange && filters.dateRange[0]) {
        params.start_date = dayjs.utc(filters.dateRange[0]).format("YYYY-MM-DD");
        params.end_date = dayjs.utc(filters.dateRange[1]).format("YYYY-MM-DD");
    }
    if (filters.registrations && filters.registrations.length > 0) {
        params.aircraft_reg = filters.registrations.join(",");
    }
    return params;
}

const PAGE_SIZE = 20;

const CROSS_FLIGHT_CODES = new Set(["V202", "V013", "V203"]);

const ValidationWarningButton = () => {
    const { filters, updateFilters, setHighlightFlightIds, flights } = useMonitor();
    const { hasPermission } = useAuth();
    const canScan = hasPermission("flight_operation:read");
    const [loading, setLoading] = useState(false);
    const [scanning, setScanning] = useState(false);
    const [scanResult, setScanResult] = useState(null);
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [summaryData, setSummaryData] = useState(null);
    const [levelFilter, setLevelFilter] = useState(null);
    const [queryRange, setQueryRange] = useState(null);

    const filtersRef = useRef(filters);
    filtersRef.current = filters;

    const skipNextRefreshRef = useRef(false);

    const fetchWarnings = useCallback(async (paramsOverride) => {
        const params = paramsOverride || buildDateParams(filtersRef.current);
        setQueryRange(params);
        setLoading(true);
        try {
            const response = await flightOperationFlightService.getValidationWarnings(params);
            setSummaryData(response?.data || null);
        } catch (error) {
            console.error("获取校验警告失败:", error);
            setSummaryData(null);
        } finally {
            setLoading(false);
        }
    }, []);

    const dateParamsKey = useMemo(() => {
        const p = buildDateParams(filters);
        return `${p.start_date || ""}|${p.end_date || ""}|${p.aircraft_reg || ""}`;
    }, [filters.dateRange, filters.registrations]);

    useEffect(() => {
        if (skipNextRefreshRef.current) {
            skipNextRefreshRef.current = false;
            return;
        }
        fetchWarnings();
    }, [dateParamsKey, fetchWarnings]);

    const handleScan = async () => {
        const params = buildDateParams(filtersRef.current);
        if (!params.start_date || !params.end_date) {
            message.warning("请先在筛选器中设置日期范围");
            return;
        }

        setScanning(true);
        setScanResult(null);
        try {
            const response = await flightOperationFlightService.runValidationScan(params);
            const data = response?.data;
            setScanResult(data);
            message.success(`扫描完成: ${data.total_scanned} 条航班，${data.problematic_count} 条有问题`);
            fetchWarnings(params);
        } catch (error) {
            console.error("批量扫描失败:", error);
            message.error("批量扫描失败: " + (error.response?.data?.detail || error.message));
        } finally {
            setScanning(false);
        }
    };

    const handleClick = () => {
        setDrawerVisible(true);
    };

    const handleRefresh = () => {
        fetchWarnings();
    };

    const handleFlightClick = (flight, relatedFlightId) => {
        const flightDay = dayjs.utc(flight.flight_date);
        if (!flightDay.isValid()) {
            message.warning("该航班日期数据异常，无法定位");
            return;
        }

        setDrawerVisible(false);

        const flightIds = relatedFlightId
            ? [flight.flight_id, relatedFlightId]
            : [flight.flight_id];

        const alreadyVisible = flightIds.every((id) => flights.some((f) => f.id === id));

        if (alreadyVisible) {
            setHighlightFlightIds(flightIds);
            return;
        }

        setHighlightFlightIds(flightIds);

        let startDay = flightDay;
        let endDay = flightDay;
        if (relatedFlightId) {
            const relatedFlight = flights.find((f) => f.id === relatedFlightId);
            if (relatedFlight && relatedFlight.flight_date) {
                const relatedDay = dayjs.utc(relatedFlight.flight_date);
                if (relatedDay.isValid()) {
                    startDay = relatedDay.isBefore(flightDay) ? relatedDay : flightDay;
                    endDay = relatedDay.isAfter(flightDay) ? relatedDay : flightDay;
                }
            } else {
                startDay = flightDay.subtract(1, "day");
            }
        }

        const startISO = startDay.startOf("day").toISOString();
        const endISO = endDay.endOf("day").toISOString();

        const newRegistrations = flight.aircraft_reg ? [flight.aircraft_reg] : filtersRef.current.registrations;
        const newParams = buildDateParams({ dateRange: [startISO, endISO], registrations: newRegistrations });
        const newKey = `${newParams.start_date || ""}|${newParams.end_date || ""}|${newParams.aircraft_reg || ""}`;
        const currentParams = buildDateParams(filtersRef.current);
        const currentKey = `${currentParams.start_date || ""}|${currentParams.end_date || ""}|${currentParams.aircraft_reg || ""}`;
        if (newKey !== currentKey) {
            skipNextRefreshRef.current = true;
        }

        const currentStatus = filtersRef.current.status || [];
        const statusWithCompleted = currentStatus.includes(FLIGHT_STATUS.COMPLETED)
            ? currentStatus
            : [...currentStatus, FLIGHT_STATUS.COMPLETED];

        const isSingleFlight = !relatedFlightId;

        updateFilters({
            ...filtersRef.current,
            status: statusWithCompleted,
            dateRange: [startISO, endISO],
            searchText: isSingleFlight ? (flight.flight_number || "") : "",
            departure_airport: isSingleFlight ? (flight.departure_airport || "") : "",
            arrival_airport: isSingleFlight ? (flight.arrival_airport || "") : "",
            registrations: newRegistrations,
        });
    };

    const totalIssues = summaryData ? summaryData.total_flights_with_warnings : 0;
    const totalErrors = summaryData ? summaryData.total_errors : 0;
    const totalWarnings = summaryData ? summaryData.total_warnings : 0;
    const hasIssues = totalIssues > 0;

    const filteredFlights =
        summaryData?.flights?.filter((f) => {
            if (!levelFilter) return true;
            return f.warnings.some((w) => w.level === levelFilter);
        }) || [];

    const buttonDanger = totalErrors > 0;

    const dateRangeLabel = queryRange?.start_date && queryRange?.end_date
        ? `${queryRange.start_date} ~ ${queryRange.end_date}`
        : "未设置日期";

    return (
        <>
            <Tooltip title={hasIssues ? `${totalErrors} 错误, ${totalWarnings} 警告` : "数据质量正常"}>
                <Badge count={totalIssues} overflowCount={99} size="small" offset={[-4, 4]}>
                    <Button
                        className={hasIssues ? "validation-btn-pulse" : ""}
                        danger={buttonDanger}
                        icon={loading ? <Spin size="small" /> : hasIssues ? <WarningOutlined /> : <SafetyCertificateOutlined />}
                        onClick={handleClick}
                        style={
                            hasIssues
                                ? totalErrors > 0
                                    ? { borderColor: "#ff4d4f", color: "#ff4d4f" }
                                    : { borderColor: "#faad14", color: "#faad14" }
                                : { borderColor: "#52c41a", color: "#52c41a" }
                        }
                    >
                        数据检查
                    </Button>
                </Badge>
            </Tooltip>

            <Drawer
                title={
                    <Space>
                        <WarningOutlined style={{ color: "#faad14" }} />
                        <span>数据检查</span>
                    </Space>
                }
                placement="right"
                width={520}
                open={drawerVisible}
                onClose={() => setDrawerVisible(false)}
            >
                {/* 扫描操作区 */}
                <div className="scan-action-bar">
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Space size={4}>
                            <Text type="secondary">范围: {dateRangeLabel}</Text>
                            {queryRange?.aircraft_reg && queryRange.aircraft_reg.split(",").map((reg) => <Tag key={reg}>{reg}</Tag>)}
                        </Space>
                        <Space>
                            <Tooltip title="用当前监控页面的筛选条件重新查询">
                                <Button size="small" icon={<ReloadOutlined />} onClick={handleRefresh} loading={loading}>
                                    刷新
                                </Button>
                            </Tooltip>
                            <Tooltip title={canScan ? "" : "需要 flight_operation:read 权限"}>
                                <Button type="primary" icon={<ScanOutlined />} loading={scanning} onClick={handleScan} disabled={!canScan}>
                                    扫描航班
                                </Button>
                            </Tooltip>
                        </Space>
                    </div>
                </div>

                {scanResult && (
                    <Alert
                        style={{ marginBottom: 12 }}
                        type={scanResult.problematic_count > 0 ? "warning" : "success"}
                        showIcon
                        message={`扫描完成: ${scanResult.total_scanned} 条航班，${scanResult.problematic_count} 条有问题，${scanResult.clean_count} 条正常`}
                        closable
                        onClose={() => setScanResult(null)}
                    />
                )}

                {/* 级别过滤 */}
                {hasIssues && (
                    <Space style={{ marginBottom: 12 }}>
                        <Button size="small" type={!levelFilter ? "primary" : "default"} onClick={() => setLevelFilter(null)}>
                            全部 ({totalIssues})
                        </Button>
                        <Button size="small" danger type={levelFilter === "ERROR" ? "primary" : "default"} onClick={() => setLevelFilter("ERROR")}>
                            ERROR ({totalErrors})
                        </Button>
                        <Button
                            size="small"
                            type={levelFilter === "WARNING" ? "primary" : "default"}
                            style={levelFilter === "WARNING" ? { background: "#faad14", borderColor: "#faad14" } : {}}
                            onClick={() => setLevelFilter("WARNING")}
                        >
                            WARNING ({totalWarnings})
                        </Button>
                    </Space>
                )}

                {/* 航班列表 */}
                {!hasIssues ? (
                    <Empty image={<CheckCircleOutlined style={{ fontSize: 48, color: "#52c41a" }} />} description="当前范围内未发现数据质量问题，点击上方按钮扫描" />
                ) : filteredFlights.length === 0 ? (
                    <Empty description="无匹配的异常航班" />
                ) : (
                    <List
                        rowKey="flight_id"
                        dataSource={filteredFlights}
                        pagination={filteredFlights.length > PAGE_SIZE ? { pageSize: PAGE_SIZE, size: "small", showTotal: (total) => `共 ${total} 条` } : false}
                        renderItem={(flight) => {
                            const dateObj = dayjs(flight.flight_date);
                            const dateLabel = dateObj.isValid() ? dateObj.format("YYYY-MM-DD") : "日期缺失";
                            const hasCrossFlight = flight.warnings.some(
                                (w) => CROSS_FLIGHT_CODES.has(w.code) && w.related_flight_id,
                            );
                            const crossFlightWarning = hasCrossFlight
                                ? flight.warnings.find((w) => CROSS_FLIGHT_CODES.has(w.code) && w.related_flight_id)
                                : null;
                            return (
                                <List.Item
                                    className="warning-flight-item"
                                    actions={[
                                        <Tooltip title={hasCrossFlight ? "定位当前航班及关联航班" : "点击定位到此航班"} key="locate">
                                            <Button
                                                type="link"
                                                size="small"
                                                icon={<SearchOutlined />}
                                                onClick={() => handleFlightClick(flight, crossFlightWarning?.related_flight_id)}
                                            >
                                                定位
                                            </Button>
                                        </Tooltip>,
                                    ]}
                                >
                                    <List.Item.Meta
                                        title={
                                            <Space size={4} wrap>
                                                <Text strong>{flight.flight_number}</Text>
                                                <Text type="secondary">{dateLabel}</Text>
                                                <Text type="secondary">
                                                    {flight.departure_airport}→{flight.arrival_airport}
                                                </Text>
                                                {flight.aircraft_reg && <Tag>{flight.aircraft_reg}</Tag>}
                                                {flight.status && flight.status !== "COMPLETED" && (
                                                    <Tag color="processing">{flight.status}</Tag>
                                                )}
                                            </Space>
                                        }
                                        description={
                                            <Space direction="vertical" size={2} style={{ width: "100%" }}>
                                                {flight.warnings.map((w, idx) => {
                                                    const config = LEVEL_CONFIG[w.level] || LEVEL_CONFIG.INFO;
                                                    const relatedInfo = w.related_flight_info;
                                                    return (
                                                        <div key={`${w.code}-${idx}`} className="warning-detail-row">
                                                            <Tag color={config.tag} icon={config.icon}>
                                                                {w.code}
                                                            </Tag>
                                                            <Text style={{ fontSize: 12 }}>{w.message}</Text>
                                                            {relatedInfo && (
                                                                <Tag color="geekblue" style={{ marginLeft: 4, fontSize: 11 }}>
                                                                    {relatedInfo.flight_number} {relatedInfo.route}
                                                                </Tag>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </Space>
                                        }
                                    />
                                </List.Item>
                            );
                        }}
                    />
                )}
            </Drawer>
        </>
    );
};

export default ValidationWarningButton;
